import React, { useState, useContext, useEffect } from 'react'
import api from '../../api-client'
import { toast } from 'react-toastify'
import { IChecklistItem, ChecklistItemType, ChecklistPhase, ChecklistOwner } from '@oneethos/shared'
import { AppContext } from '../../appContext'
import actions from '../../actions/checklistItemActions'
import { selectChecklistItemsByPhase } from '../../selectors/checklistItems'
import ChecklistItemForm from './checklist-item-form'
import ChecklistItemsLoanApplicationList from './checklist-loan-application-list'
import ChecklistItemsLoanFundingList from './checklist-loan-funding-list'

interface ChecklistItemsConfigProps {
  currentPhase: ChecklistPhase
}

export const ChecklistItemsConfig = ({ currentPhase }: ChecklistItemsConfigProps) => {
  const [isAdding, setIsAdding] = useState(false)
  const [editingItem, setEditingItem] = useState<IChecklistItem | null>(null)
  const [newItem, setNewItem] = useState<Partial<IChecklistItem>>({})
  const isLoanFundingPhase = currentPhase === ChecklistPhase.LoanFunding

  const { state, dispatch } = useContext(AppContext)
  const checklistItems = selectChecklistItemsByPhase(state, currentPhase)
  const saving = state.checklistItemsConfig.saving

  useEffect(() => {
    setNewItem({})
    setEditingItem(null)
    setIsAdding(false)
  }, [currentPhase])

  const onSave = async () => {
    try {
      dispatch(actions.saveChecklistItemRequest())
      const saved = await api.post('/checklist-items', newItem)
      dispatch(actions.saveChecklistItemSuccess(saved))
      toast.success('Checklist item created successfully')
      cancelEditing()
    } catch (error: any) {
      dispatch(actions.saveChecklistItemError(error?.error || 'Failed to save'))
      toast.error('Failed to save checklist item')
    }
  }

  const onEdit = async () => {
    try {
      dispatch(actions.saveChecklistItemRequest())
      const updated = await api.put(`/checklist-items/${editingItem.id}`, newItem)
      dispatch(actions.saveChecklistItemSuccess(updated))
      toast.success('Checklist item updated successfully')
      cancelEditing()
    } catch (error: any) {
      dispatch(actions.saveChecklistItemError(error?.error || 'Failed to update'))
      toast.error('Failed to update checklist item')
    }
  }

  const startAdding = () => {
    setNewItem({
      label: '',
      description: '',
      type: ChecklistItemType.System,
      phase: currentPhase,
      owner: ChecklistOwner.Installer,
      dataField: '',
      active: true,
      fundReleaseTypes: [],
      agentWorkflowId: null,
      agentWorkflowEnabled: false,
      presubmitAgentWorkflowId: null,
      sequence: null
    })
    setEditingItem(null)
    setIsAdding(true)
  }

  const startEditing = (item: IChecklistItem) => {
    setEditingItem(item)
    setNewItem({
      label: item.label,
      description: item.description,
      type: item.type,
      phase: item.phase,
      owner: item.owner,
      dataField: item.dataField,
      active: item.active,
      fundReleaseTypes: item.fundReleaseTypes || [],
      agentWorkflowId: item.agentWorkflowId,
      agentWorkflowEnabled: item.agentWorkflowEnabled,
      presubmitAgentWorkflowId: item.presubmitAgentWorkflowId,
      sequence: item.sequence
    })
    setIsAdding(true)
  }

  const cancelEditing = () => {
    setIsAdding(false)
    setEditingItem(null)
  }

  return (
    <>
      {isAdding ? (
        <ChecklistItemForm
          newItem={newItem}
          setNewItem={setNewItem}
          editingItem={editingItem}
          saving={saving}
          isLoanFundingPhase={isLoanFundingPhase}
          onSave={onSave}
          onEdit={onEdit}
          onCancel={cancelEditing}
        />
      ) : (
        <>
          {currentPhase === ChecklistPhase.LoanApplication && (
            <ChecklistItemsLoanApplicationList
              checklistItems={checklistItems}
              onStartEditing={startEditing}
              onStartAdding={startAdding}
              isAdding={isAdding}
            />
          )}
          {currentPhase === ChecklistPhase.LoanFunding && (
            <ChecklistItemsLoanFundingList
              checklistItems={checklistItems}
              onStartEditing={startEditing}
              onStartAdding={startAdding}
              isAdding={isAdding}
            />
          )}
        </>
      )}
    </>
  )
}
